import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isPublicationOpen } from '@reelmachine/core';
import { useConnectZernio, useCurrentOrg, useReel, useReelAction, useSetCaption } from '../api/hooks';
import { useDebouncedCallback } from '../lib/useDebounced';
import { Icon } from '../ui/Icon';
import { ErrorView, HistoryButton, Loading, Screen } from '../ui/Screen';
import { Stepper } from '../ui/Stepper';
import { useToast } from '../ui/Toast';
import {
  EligibilityList,
  PUBLICATION_LABEL,
  PublicationCard,
  QualityCard,
  publicationView,
  type ReasonAction,
} from './PostChecks';

export const CAPTION_MAX = 2200;

export function PostScreen() {
  const { reelId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const q = useReel(reelId);
  const { org, orgId, me } = useCurrentOrg();
  const staff = me.data?.staff ?? false;
  const setCaption = useSetCaption(reelId);
  const publish = useReelAction(reelId, 'publish');
  const recheck = useReelAction(reelId, 'recheckQuality');
  const render = useReelAction(reelId, 'render');
  const connect = useConnectZernio(orgId ?? '');
  const [caption, setLocal] = useState<string | null>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);

  const serverCaption = q.data?.reel.caption ?? '';
  useEffect(() => {
    setLocal((c) => (c === null && q.data ? serverCaption : c));
  }, [q.data, serverCaption]);

  const [saveCaption, flushCaption] = useDebouncedCallback((value: string) => {
    setCaption.mutate(value, { onError: (e) => toast(`Caption not saved. ${e.message}`) });
  }, 800);

  if (q.isError) return <ErrorView back="/" error={q.error} />;
  if (q.isPending) return <Loading />;
  const detail = q.data;
  const { reel } = detail;
  const text = caption ?? serverCaption;
  const connected = org?.zernio?.status === 'connected';
  const pub = publicationView(reel.publication, reel);
  const open = isPublicationOpen(reel.publication);
  const locked = open || pub === 'published';
  const dirty = text !== serverCaption;
  // Older APIs send no eligibility: fall back to the checks they did.
  const eligible = detail.eligibility ? detail.eligibility.ok : connected && !!serverCaption.trim();

  const postNow = async () => {
    flushCaption();
    try {
      if (text !== reel.caption) await setCaption.mutateAsync(text);
      await publish.mutateAsync();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not post.');
    }
  };

  const run = (fn: () => Promise<unknown>, to?: string) => async () => {
    try {
      await fn();
      if (to) navigate(to);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Something went wrong.');
    }
  };

  const connectNow = async () => {
    try {
      const { url } = await connect.mutateAsync();
      window.location.assign(url);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not open Instagram.');
    }
  };

  const onAction = (a: ReasonAction) => {
    switch (a) {
      case 'makeVideo':
        navigate(`/reels/${reelId}/video`);
        break;
      case 'remake':
        void run(() => render.mutateAsync(), `/reels/${reelId}/video`)();
        break;
      case 'recheck':
        void run(() => recheck.mutateAsync())();
        break;
      case 'script':
        navigate(`/reels/${reelId}/script`);
        break;
      case 'caption':
        captionRef.current?.focus();
        break;
    }
  };

  const bar =
    pub === 'published' ? (
      <button type="button" className="btn" onClick={() => navigate(`/reels/${reelId}/stats`)}>
        See stats
      </button>
    ) : open && pub ? (
      <button type="button" className="btn" disabled>
        {PUBLICATION_LABEL[pub]}
      </button>
    ) : (
      <button
        type="button"
        className="btn"
        disabled={!eligible || dirty || publish.isPending || setCaption.isPending}
        onClick={() => void postNow()}
      >
        {publish.isPending ? 'Posting…' : pub === 'rejected' ? 'Post again' : 'Post now'}
      </button>
    );
  const barHint = locked
    ? undefined
    : !connected
      ? 'Connect Instagram to post.'
      : dirty
        ? 'Saving caption…'
        : !eligible
          ? 'Fix the points above to post.'
          : undefined;

  return (
    <Screen title={reel.title} back="/" right={<HistoryButton reelId={reelId} />} bar={bar} barHint={barHint}>
      <Stepper reel={reel} current="post" />
      <PublicationCard detail={detail} staff={staff} />
      {!locked && detail.eligibility && (
        <EligibilityList
          eligibility={detail.eligibility}
          onAction={onAction}
          busy={render.isPending || recheck.isPending}
        />
      )}
      <div className="field">
        <div className="row between">
          <label className="label" htmlFor="caption">
            Caption
          </label>
          <span className="nums" aria-live="polite">
            {text.length}/{CAPTION_MAX}
          </span>
        </div>
        <textarea
          id="caption"
          ref={captionRef}
          readOnly={locked}
          rows={9}
          maxLength={CAPTION_MAX}
          value={text}
          onChange={(e) => {
            setLocal(e.target.value);
            saveCaption(e.target.value);
          }}
        />
      </div>
      <div className="card">
        <div className="row between nowrap">
          <div>
            <span className="label">Instagram</span>
            <h3>{org?.instagramHandle ?? 'No channel set'}</h3>
          </div>
          {connected ? (
            <span className="chip ok">
              <Icon name="check" /> Zernio
            </span>
          ) : (
            <button
              type="button"
              className="iconbtn"
              disabled={connect.isPending || !orgId}
              onClick={() => void connectNow()}
            >
              Connect Instagram
            </button>
          )}
        </div>
      </div>
      {pub !== 'published' && (
        <QualityCard
          detail={detail}
          staff={staff}
          locked={locked}
          recheck={() => void run(() => recheck.mutateAsync())()}
          rechecking={recheck.isPending}
        />
      )}
    </Screen>
  );
}
